import React from 'react';

/**
 * MobileRecordList — renders a list of records as stacked, tap-through cards
 * instead of a dense multi-column table (which, on a phone, can only scroll
 * horizontally and is effectively unusable). Each card shows a title, an
 * optional status pill, and up to a few summary lines; tapping it fires
 * onSelect so the page can open a full-detail sheet/modal.
 *
 * Generic over the record type so each pillar maps its own fields.
 */

export interface MobileRecordCard {
  /** Stable key (e.g. record id). */
  id: string;
  /** Primary line — the record's name/identity. */
  title: string;
  /** Optional status pill shown top-right. */
  status?: { label: string; tone?: 'success' | 'warning' | 'neutral' | 'danger' };
  /** Up to a few muted summary lines folding the remaining columns. */
  lines?: string[];
}

const TONE: Record<string, { bg: string; fg: string }> = {
  success: { bg: '#dcfce7', fg: '#15803d' },
  warning: { bg: '#fef3c7', fg: '#b45309' },
  danger: { bg: '#fee2e2', fg: '#b91c1c' },
  neutral: { bg: '#f1f5f9', fg: '#475569' },
};

export function MobileRecordList({
  records,
  onSelect,
  emptyText = 'Nothing to show.',
}: {
  records: MobileRecordCard[];
  onSelect?: (id: string) => void;
  emptyText?: string;
}) {
  if (records.length === 0) {
    return <div style={styles.empty}>{emptyText}</div>;
  }

  return (
    <div style={styles.list}>
      {records.map((r) => {
        const tone = r.status ? TONE[r.status.tone || 'neutral'] : null;
        const clickable = !!onSelect;
        return (
          <button
            key={r.id}
            type="button"
            style={{ ...styles.card, ...(clickable ? styles.cardClickable : styles.cardStatic) }}
            onClick={clickable ? () => onSelect!(r.id) : undefined}
            aria-label={clickable ? `Open ${r.title}` : undefined}
          >
            <div style={styles.cardTop}>
              <span style={styles.title}>{r.title}</span>
              {r.status && tone && (
                <span style={{ ...styles.pill, backgroundColor: tone.bg, color: tone.fg }}>
                  {r.status.label}
                </span>
              )}
            </div>
            {(r.lines || []).map((line, i) => (
              <div key={i} style={styles.line}>
                {line}
              </div>
            ))}
            {clickable && <span style={styles.chevron} aria-hidden>›</span>}
          </button>
        );
      })}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  list: { display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 0 8px' },
  card: {
    position: 'relative',
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    textAlign: 'left',
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: '12px 14px',
    font: 'inherit',
  },
  cardClickable: { cursor: 'pointer' },
  cardStatic: { cursor: 'default' },
  cardTop: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 4,
    paddingRight: 14,
  },
  title: { fontSize: '0.95rem', fontWeight: 800, color: '#0f2d1f', lineHeight: 1.2 },
  pill: {
    flexShrink: 0,
    fontSize: '0.66rem',
    fontWeight: 700,
    padding: '3px 9px',
    borderRadius: 999,
    whiteSpace: 'nowrap',
  },
  line: { fontSize: '0.8rem', color: '#6b7280', lineHeight: 1.5 },
  chevron: {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#9ca3af',
    fontSize: '1.1rem',
  },
  empty: { textAlign: 'center', color: '#6b7280', padding: '28px 12px', fontSize: '0.9rem' },
};
